import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Upload, FileText, Clock, CheckCircle, AlertCircle, 
  Loader2, Calendar, User, MapPin, Sparkles, Eye, Edit, FilePlus, ClipboardList
} from 'lucide-react';
import {
  extractTimesheetData, extractClientPOData, confirmOCRExtraction,
  getOCRExtraction, listOCRExtractions, createTimesheetFromExtraction,
  createClientPOFromExtraction, getCompanies
} from '../lib/api';

export const OCRExtractionPage = () => {
  const [activeTab, setActiveTab] = useState('timesheet');
  const [uploading, setUploading] = useState(false);
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [creatingRecord, setCreatingRecord] = useState(false);
  
  // Extraction result state
  const [extractionResult, setExtractionResult] = useState(null);
  const [currentExtraction, setCurrentExtraction] = useState(null); // Full extraction record
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);

  const loadExtractions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listOCRExtractions({ extraction_type: activeTab === 'timesheet' ? 'timesheet' : 'client_po' });
      setExtractions(response.data);
    } catch (error) {
      console.error('Failed to load extractions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompanyId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  useEffect(() => {
    loadExtractions();
    loadCompanies();
  }, [loadExtractions]);

  const handleFileUpload = async (event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WEBP, or PDF.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    try {
      let response;
      if (type === 'timesheet') {
        response = await extractTimesheetData(file);
      } else {
        response = await extractClientPOData(file);
      }

      setExtractionResult(response.data);
      setEditedData(response.data.extracted_data);
      setShowResultDialog(true);
      toast.success('Document processed successfully!');
      loadExtractions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process document');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleConfirm = async () => {
    if (!extractionResult) return;

    try {
      await confirmOCRExtraction(extractionResult.extraction_id, editMode ? editedData : null);
      toast.success('Extraction confirmed!');
      setShowResultDialog(false);
      setExtractionResult(null);
      setCurrentExtraction(null);
      setEditMode(false);
      loadExtractions();
    } catch (error) {
      toast.error('Failed to confirm extraction');
    }
  };

  const handleViewExtraction = async (extractionId) => {
    try {
      const response = await getOCRExtraction(extractionId);
      setCurrentExtraction(response.data);
      setExtractionResult({ extraction_id: extractionId, extracted_data: response.data.extracted_data });
      setEditedData(response.data.extracted_data);
      setShowResultDialog(true);
    } catch (error) {
      toast.error('Failed to load extraction details');
    }
  };

  const handleCreateTimesheet = async () => {
    if (!extractionResult || !selectedCompanyId) {
      toast.error('Please select a company');
      return;
    }

    setCreatingRecord(true);
    try {
      const response = await createTimesheetFromExtraction(
        extractionResult.extraction_id,
        selectedCompanyId,
        currentExtraction?.delivery_note_id,
        currentExtraction?.client_po_id
      );
      toast.success(`Timesheet created: ${response.data.data.hours_worked} hours for ${response.data.data.client_name || 'client'}`);
      setShowResultDialog(false);
      setExtractionResult(null);
      setCurrentExtraction(null);
      loadExtractions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    } finally {
      setCreatingRecord(false);
    }
  };

  const handleCreateClientPO = async () => {
    if (!extractionResult || !selectedCompanyId) {
      toast.error('Please select a company');
      return;
    }

    setCreatingRecord(true);
    try {
      const response = await createClientPOFromExtraction(
        extractionResult.extraction_id,
        selectedCompanyId,
        null // client_id - can be added later
      );
      toast.success(`Client PO created: ${response.data.po_number}`);
      setShowResultDialog(false);
      setExtractionResult(null);
      setCurrentExtraction(null);
      loadExtractions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Client PO');
    } finally {
      setCreatingRecord(false);
    }
  };

  const renderTimesheetData = (data) => {
    if (!data || data.parse_error) {
      return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-700 mb-2">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Extraction needs manual review</span>
          </div>
          <pre className="text-xs overflow-auto max-h-60 bg-white p-2 rounded">
            {data?.raw_response || 'No data extracted'}
          </pre>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Confidence Score */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">AI Confidence</span>
          <Badge className={data.confidence_score >= 80 ? 'bg-green-100 text-green-800' : 
                           data.confidence_score >= 50 ? 'bg-amber-100 text-amber-800' : 
                           'bg-red-100 text-red-800'}>
            {data.confidence_score}%
          </Badge>
        </div>

        {/* Period */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Period Start</Label>
            {editMode ? (
              <Input 
                value={editedData?.period_start || ''} 
                onChange={(e) => setEditedData({...editedData, period_start: e.target.value})}
              />
            ) : (
              <p className="font-medium">{data.period_start || 'N/A'}</p>
            )}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Period End</Label>
            {editMode ? (
              <Input 
                value={editedData?.period_end || ''} 
                onChange={(e) => setEditedData({...editedData, period_end: e.target.value})}
              />
            ) : (
              <p className="font-medium">{data.period_end || 'N/A'}</p>
            )}
          </div>
        </div>

        {/* Equipment */}
        {data.equipment_details?.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground">Equipment</Label>
            <div className="space-y-1 mt-1">
              {data.equipment_details.map((eq, idx) => (
                <div key={idx} className="text-sm bg-muted p-2 rounded">
                  <span className="font-medium">{eq.description}</span>
                  {eq.registration_number && <span className="text-muted-foreground ml-2">({eq.registration_number})</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Hours */}
        {data.daily_hours?.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground">Daily Hours</Label>
            <div className="border rounded mt-1 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-2 py-1 text-left">Date</th>
                    <th className="px-2 py-1 text-right">Hours</th>
                    <th className="px-2 py-1 text-right">OT</th>
                    <th className="px-2 py-1 text-left">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily_hours.map((day, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-2 py-1">{day.date}</td>
                      <td className="px-2 py-1 text-right">{day.hours_worked}</td>
                      <td className="px-2 py-1 text-right">{day.overtime_hours || 0}</td>
                      <td className="px-2 py-1 text-xs text-muted-foreground">{day.remarks || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="grid grid-cols-3 gap-4 p-3 bg-muted rounded-lg">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Regular Hours</p>
            {editMode ? (
              <Input 
                type="number"
                className="h-8 text-center"
                value={editedData?.total_regular_hours || 0} 
                onChange={(e) => setEditedData({...editedData, total_regular_hours: parseFloat(e.target.value)})}
              />
            ) : (
              <p className="text-xl font-bold">{data.total_regular_hours || 0}</p>
            )}
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Overtime Hours</p>
            {editMode ? (
              <Input 
                type="number"
                className="h-8 text-center"
                value={editedData?.total_overtime_hours || 0} 
                onChange={(e) => setEditedData({...editedData, total_overtime_hours: parseFloat(e.target.value)})}
              />
            ) : (
              <p className="text-xl font-bold">{data.total_overtime_hours || 0}</p>
            )}
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Total Hours</p>
            {editMode ? (
              <Input 
                type="number"
                className="h-8 text-center"
                value={editedData?.total_hours || 0} 
                onChange={(e) => setEditedData({...editedData, total_hours: parseFloat(e.target.value)})}
              />
            ) : (
              <p className="text-xl font-bold text-primary">{data.total_hours || 0}</p>
            )}
          </div>
        </div>

        {/* Client Signature */}
        <div className="flex items-center gap-2">
          <span className="text-sm">Client Signature:</span>
          {data.client_signature_present ? (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" /> Present
            </Badge>
          ) : (
            <Badge className="bg-red-100 text-red-800">
              <AlertCircle className="h-3 w-3 mr-1" /> Not Found
            </Badge>
          )}
        </div>

        {/* Additional Info */}
        {(data.client_name || data.project_name || data.location) && (
          <div className="grid grid-cols-3 gap-2 text-sm">
            {data.client_name && (
              <div>
                <span className="text-muted-foreground">Client:</span>
                <p className="font-medium">{data.client_name}</p>
              </div>
            )}
            {data.project_name && (
              <div>
                <span className="text-muted-foreground">Project:</span>
                <p className="font-medium">{data.project_name}</p>
              </div>
            )}
            {data.location && (
              <div>
                <span className="text-muted-foreground">Location:</span>
                <p className="font-medium">{data.location}</p>
              </div>
            )}
          </div>
        )}

        {data.additional_notes && (
          <div>
            <Label className="text-xs text-muted-foreground">Additional Notes</Label>
            <p className="text-sm mt-1">{data.additional_notes}</p>
          </div>
        )}
      </div>
    );
  };

  const renderClientPOData = (data) => {
    if (!data || data.parse_error) {
      return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-700 mb-2">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Extraction needs manual review</span>
          </div>
          <pre className="text-xs overflow-auto max-h-60 bg-white p-2 rounded">
            {data?.raw_response || 'No data extracted'}
          </pre>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Confidence & PO Number */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs text-muted-foreground">PO Number</Label>
            <p className="text-xl font-bold">{data.po_number || 'Not found'}</p>
          </div>
          <Badge className={data.confidence_score >= 80 ? 'bg-green-100 text-green-800' : 
                           data.confidence_score >= 50 ? 'bg-amber-100 text-amber-800' : 
                           'bg-red-100 text-red-800'}>
            {data.confidence_score}% confidence
          </Badge>
        </div>

        {/* Client Info */}
        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Client Name:</span>
                <p className="font-medium">{data.client_name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">PO Date:</span>
                <p className="font-medium">{data.po_date || 'N/A'}</p>
              </div>
              {data.client_contact && (
                <div>
                  <span className="text-muted-foreground">Contact:</span>
                  <p className="font-medium">{data.client_contact}</p>
                </div>
              )}
              {data.project_name && (
                <div>
                  <span className="text-muted-foreground">Project:</span>
                  <p className="font-medium">{data.project_name}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        {data.line_items?.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground">Line Items</Label>
            <div className="border rounded mt-1 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-2 py-1 text-left">Description</th>
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-right">Rate</th>
                    <th className="px-2 py-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.line_items.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-2 py-1">{item.description}</td>
                      <td className="px-2 py-1 text-right">{item.quantity} {item.unit}</td>
                      <td className="px-2 py-1 text-right">{item.unit_rate?.toLocaleString()}</td>
                      <td className="px-2 py-1 text-right font-medium">{item.amount?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64 space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{data.currency} {data.subtotal?.toLocaleString()}</span>
            </div>
            {data.vat_amount && (
              <div className="flex justify-between">
                <span>VAT ({data.vat_percentage}%):</span>
                <span>{data.currency} {data.vat_amount?.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between font-bold border-t pt-1">
              <span>Total:</span>
              <span>{data.currency} {data.total_amount?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="ocr-extraction-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-purple-600" />
            AI Document Extraction
          </h1>
          <p className="text-muted-foreground">Upload timesheets or POs to auto-extract data using AI</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="timesheet" data-testid="tab-timesheet">
            <Clock className="h-4 w-4 mr-2" />
            Timesheets
          </TabsTrigger>
          <TabsTrigger value="client_po" data-testid="tab-client-po">
            <FileText className="h-4 w-4 mr-2" />
            Client POs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timesheet" className="space-y-4">
          {/* Upload Card */}
          <Card className="border-2 border-dashed">
            <CardContent className="pt-6">
              <div className="text-center">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">Upload Client Timesheet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a photo or PDF of the signed client timesheet. 
                  AI will extract hours, dates, equipment, and signature status.
                </p>
                <div className="flex justify-center">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'timesheet')}
                      disabled={uploading}
                      data-testid="timesheet-file-input"
                    />
                    <Button disabled={uploading} asChild>
                      <span>
                        {uploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Select File
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Supported: JPEG, PNG, WEBP, PDF (max 10MB)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Recent Extractions */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Timesheet Extractions</CardTitle>
              <CardDescription>View and manage extracted timesheet data</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : extractions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No timesheet extractions yet. Upload a document to get started.
                </div>
              ) : (
                <div className="space-y-2">
                  {extractions.map(ext => (
                    <div 
                      key={ext.id} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer"
                      onClick={() => handleViewExtraction(ext.id)}
                      data-testid={`extraction-${ext.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{ext.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(ext.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={ext.confirmed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {ext.confirmed ? 'Confirmed' : 'Pending Review'}
                        </Badge>
                        <Button size="sm" variant="ghost">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="client_po" className="space-y-4">
          {/* Upload Card */}
          <Card className="border-2 border-dashed">
            <CardContent className="pt-6">
              <div className="text-center">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">Upload Client Purchase Order</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a photo or PDF of the client PO. 
                  AI will extract PO number, items, rates, and client details.
                </p>
                <div className="flex justify-center">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'client_po')}
                      disabled={uploading}
                      data-testid="client-po-file-input"
                    />
                    <Button disabled={uploading} asChild>
                      <span>
                        {uploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Select File
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Supported: JPEG, PNG, WEBP, PDF (max 10MB)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Recent Extractions */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Client PO Extractions</CardTitle>
              <CardDescription>View and manage extracted PO data</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : extractions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No PO extractions yet. Upload a document to get started.
                </div>
              ) : (
                <div className="space-y-2">
                  {extractions.map(ext => (
                    <div 
                      key={ext.id} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer"
                      onClick={() => handleViewExtraction(ext.id)}
                      data-testid={`extraction-${ext.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{ext.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {ext.extracted_data?.po_number && `PO: ${ext.extracted_data.po_number} • `}
                            {new Date(ext.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={ext.confirmed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {ext.confirmed ? 'Confirmed' : 'Pending Review'}
                        </Badge>
                        <Button size="sm" variant="ghost">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Extraction Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI Extracted Data
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {extractionResult && (
              activeTab === 'timesheet' 
                ? renderTimesheetData(editMode ? editedData : extractionResult.extracted_data)
                : renderClientPOData(editMode ? editedData : extractionResult.extracted_data)
            )}
          </div>

          {/* Company Selection for Record Creation */}
          {currentExtraction?.confirmed && !currentExtraction?.timesheet_created && !currentExtraction?.client_po_created && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <FilePlus className="h-5 w-5" />
                <span className="font-medium">Create Record from Extraction</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-xs text-green-700">Select Company</Label>
                  <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                    <SelectTrigger className="bg-white" data-testid="select-company">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(company => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {activeTab === 'timesheet' ? (
                  <Button
                    onClick={handleCreateTimesheet}
                    disabled={creatingRecord || !selectedCompanyId}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="create-timesheet-btn"
                  >
                    {creatingRecord ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Clock className="h-4 w-4 mr-2" />
                    )}
                    Create Timesheet
                  </Button>
                ) : (
                  <Button
                    onClick={handleCreateClientPO}
                    disabled={creatingRecord || !selectedCompanyId}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="create-client-po-btn"
                  >
                    {creatingRecord ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ClipboardList className="h-4 w-4 mr-2" />
                    )}
                    Create Client PO
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Already Created Badge */}
          {currentExtraction?.timesheet_created && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700">
              <CheckCircle className="h-5 w-5" />
              <span>Timesheet already created from this extraction</span>
            </div>
          )}
          {currentExtraction?.client_po_created && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700">
              <CheckCircle className="h-5 w-5" />
              <span>Client PO already created from this extraction</span>
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => setEditMode(!editMode)}
              data-testid="edit-mode-btn"
            >
              <Edit className="h-4 w-4 mr-2" />
              {editMode ? 'Cancel Edit' : 'Edit Data'}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowResultDialog(false)}>
                Close
              </Button>
              {!currentExtraction?.confirmed && (
                <Button onClick={handleConfirm} data-testid="confirm-extraction-btn">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Data
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OCRExtractionPage;
